/**
 * API client for the auto-booking review board (backend: apps/oh_autobooking).
 *
 * The board shows a FROZEN dry run — the numbers were priced when the run was
 * taken and do not move while centres comment on them. Nothing here books or
 * charges; the only writes are the two comment fields and the review status.
 *
 * Every `apiEndpoints.X` reference lives INSIDE a function body (TDZ-safety —
 * mirrors issues.js), and every call returns `response.data`, the unwrapped
 * body, because the request helper resolves to the FULL axios response.
 */
import { sentryLogger } from 'sentry'

import { apiEndpoints } from './common'

import { makeRequestUrl } from '../app/helpers'
import { get, patch } from '../request'

/** The cycles that have a visible run — drives the month picker. */
const fetchAutoBookingRuns = async (queries = {}) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.autoBooking}/runs/alfred`, queries)
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'fetchAutoBookingRuns')
    throw error
  }
}

/**
 * One page of parents for a cycle.
 * `queries` accepts cycle (required), rail, centre, outcome, review_status,
 * has_shortfall, will_terminate, no_discount, commented, search, limit, offset.
 */
const fetchAutoBookingParents = async (queries = {}) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.autoBooking}/parents/alfred`, queries)
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'fetchAutoBookingParents')
    throw error
  }
}

/** Per-centre review progress, so a lead can see how far their centre has got. */
const fetchAutoBookingSummary = async (queries = {}) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.autoBooking}/summary/alfred`, queries)
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'fetchAutoBookingSummary')
    throw error
  }
}

/**
 * Save a review. Send only what changed — the backend stamps who and when per
 * field, so posting an unchanged `hq_comment` would rewrite its author.
 * `hq_comment` is rejected for non-admins by the backend.
 */
const updateAutoBookingReview = async (uuid, payload) => {
  try {
    const response = await patch(
      `${apiEndpoints.autoBooking}/parents/${uuid}/alfred`,
      payload
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'updateAutoBookingReview')
    throw error
  }
}

export {
  fetchAutoBookingRuns,
  fetchAutoBookingParents,
  fetchAutoBookingSummary,
  updateAutoBookingReview
}
